import React, { useEffect, useMemo, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import {
  ANNOUNCEMENTS_SERVER_NAME,
  HOME_SERVER_TAB_ID,
  addPeopleToGroup,
  adjustXpPoints,
  backupChatKeysToCloud,
  canPostAnnouncementProfile,
  canUseModeratorAction,
  clearChatTimeout,
  consumeModeratorActionQuota,
  describeXpEvent,
  describeMessagePreview,
  deleteMessageForEveryone,
  ensureAnnouncementsChannel,
  ensureProfile,
  editMessage,
  isAnnouncementChannelConversation,
  isComputerProfile,
  isModeratorProfile,
  isChatAdminIdentity,
  isChatAccessDisabledProfile,
  isProfileTimedOut,
  leaveGroup,
  moderationLimitsForProfile,
  moderationQuotaForToday,
  moderationRoleLabel,
  removeGroupMember,
  recoverChatKeysFromCloud,
  setClownRole,
  removeComputerFromWebsite,
  deleteChatAccountPermanently,
  deleteChatGroupPermanently,
  setMemberModerationRole,
  subscribeAnnouncementChannels,
  subscribeCloudKeyBackupStatus,
  subscribeMyConversations,
  subscribeMessages,
  subscribeXpEvents,
  sendMessage,
  warmConversationSendCache,
  hideMessageForMe,
  setChatTimeout,
  setChatAccessDisabled,
  toggleReaction,
  transferGroupOwnership,
  getOrCreateDm,
  createGroup,
  subscribeChatProfiles,
  subscribeTyping,
  setTyping,
  subscribeReadReceipts,
  markConversationRead,
  setConversationLastRead,
  setUserStatus,
  setConversationArchived,
  setConversationHidden,
  CHAT_STATUSES,
  signOutChat,
  subscribeConversationPrefs,
  tsMs,
  xpLevelInfoForProfile,
  xpPointsForProfile,
  xpRewardForProfile,
} from "./chatService";
import ChatLogin from "./ChatLogin";
import ChatSidebar from "./ChatSidebar";
import ChatMessages from "./ChatMessages";
import ForwardMessageModal from "./ForwardMessageModal";
import MessageInput from "./MessageInput";
import ModeratorPanelModal from "./ModeratorPanelModal";
import NewChatModal from "./NewChatModal";
import XpPointsModal from "./XpPointsModal";
import { avatarColor, avatarInitial } from "./avatar";
import "./chat.css";

const PROFILE_BOOT_TIMEOUT_MS = 9000;
const PHONE_UI_BREAKPOINT_PX = 900;
const EMPTY_ZK_BACKUP_STATUS = Object.freeze({
  exists: false,
  updatedAt: null,
  createdAt: null,
});

function detectPhoneUi() {
  if (typeof window === "undefined") return false;
  const isSmallViewport = window.matchMedia(`(max-width: ${PHONE_UI_BREAKPOINT_PX}px)`).matches;
  const isTouchOnly = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
  return isSmallViewport || isTouchOnly;
}

function withTimeout(promise, timeoutMs, message) {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timeoutId));
}

export default function ChatPage({ secretAdminOnly = false }) {
  const [authReady, setAuthReady] = useState(false);
  const [me, setMe] = useState(null);   // { uid, email, displayName, loginName }
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [profilesByUid, setProfilesByUid] = useState({});
  const [typingUsers, setTypingUsers] = useState([]);
  const [readReceipts, setReadReceipts] = useState({});
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchIndex, setSearchIndex] = useState(0);
  const [replyTo, setReplyTo] = useState(null);
  const [forwardMessage, setForwardMessage] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [statusMode, setStatusMode] = useState(() => {
    const saved = localStorage.getItem("chat.status");
    return CHAT_STATUSES[saved] ? saved : "online";
  });
  const [activePanel, setActivePanel] = useState(null);
  const [chatSettings, setChatSettings] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("chat.settings") || "{}");
      return {
        accent: saved.accent || "#8ab4f8",
        density: saved.density || "comfortable",
        theme: saved.theme || "dark",
      };
    } catch {
      return { accent: "#8ab4f8", density: "comfortable", theme: "dark" };
    }
  });
  const [showNew, setShowNew] = useState(false);
  const [showModeratorPanel, setShowModeratorPanel] = useState(false);
  const [showXpPointsModal, setShowXpPointsModal] = useState(false);
  const [addPeopleTarget, setAddPeopleTarget] = useState(null);
  const [showModCongratsToast, setShowModCongratsToast] = useState(false);
  const [xpEvents, setXpEvents] = useState([]);
  const [xpToast, setXpToast] = useState(null);
  const [conversationPrefsById, setConversationPrefsById] = useState({});
  const [zkBackupStatus, setZkBackupStatus] = useState(() => ({ ...EMPTY_ZK_BACKUP_STATUS }));
  const [zkBackupStatusError, setZkBackupStatusError] = useState("");
  const [blockedConversationIds, setBlockedConversationIds] = useState([]);
  const [serverConversations, setServerConversations] = useState([]);
  const [activeServerId, setActiveServerId] = useState(HOME_SERVER_TAB_ID);
  const [permissionError, setPermissionError] = useState(null);
  const [isPhoneUi, setIsPhoneUi] = useState(() => detectPhoneUi());
  const [phoneSidebarOpen, setPhoneSidebarOpen] = useState(() => detectPhoneUi());
  const lastTypingSentRef = useRef(0);
  const typingTimerRef = useRef(null);
  const profilesByUidRef = useRef({});
  const serverConversationIdsRef = useRef(new Set());
  const latestXpEventIdRef = useRef("");
  const xpFeedBootedRef = useRef(false);

  const meProfileForEffects = useMemo(
    () => (me
      ? { ...me, ...(profilesByUid[me.uid] || {}), status: statusMode }
      : null),
    [me, profilesByUid, statusMode]
  );
  const canPostAnnouncements = canPostAnnouncementProfile(meProfileForEffects || {});

  function clearPermissionErrorFor(where) {
    setPermissionError((prev) => {
      if (!prev) return null;
      const prevWhere = typeof prev.where === "string" ? prev.where : "";
      if (!where) return prevWhere ? prev : null;
      return prevWhere === where ? null : prev;
    });
  }

  function setPermissionErrorFor(where, err) {
    if (!err) return;
    if (where && !err.where) {
      try { err.where = where; } catch (_) {}
    }
    setPermissionError(err);
  }

  // ── Auth state ───────────────────────────────────────────────────────
  useEffect(() => {
    return onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setMe(null);
        setConversations([]);
        setActiveId(null);
        setMessages([]);
        setTypingUsers([]);
        setReadReceipts({});
        setConversationPrefsById({});
        setBlockedConversationIds([]);
        setServerConversations([]);
        setActiveServerId(HOME_SERVER_TAB_ID);
        setReplyTo(null);
        setForwardMessage(null);
        setEditingMessage(null);
        setAddPeopleTarget(null);
        setShowModeratorPanel(false);
        setShowXpPointsModal(false);
        setShowModCongratsToast(false);
        setXpEvents([]);
        setXpToast(null);
        setZkBackupStatus({ ...EMPTY_ZK_BACKUP_STATUS });
        setZkBackupStatusError("");
        setPermissionError(null);
        setAuthReady(true);
        return;
      }

      const fallbackMe = {
        uid: user.uid,
        email: user.email || "",
        loginName: user.email ? user.email.split("@")[0] : "",
        displayName: user.email ? user.email.split("@")[0] : "You",
      };

      // Render chat UI immediately even if Firestore profile hydration is slow.
      setMe(fallbackMe);
      setAuthReady(true);

      // Self-heal a chatProfile if missing (e.g. user signed up via the existing site auth before).
      // If Firestore rules aren't deployed yet this will throw — surface it as a banner instead of crashing.
      try {
        const profile = await withTimeout(
          ensureProfile(user, user.email?.split("@")[0]),
          PROFILE_BOOT_TIMEOUT_MS,
          "Chat profile sync timed out. Firestore may be throttled right now.",
        );
        setMe({
          uid: user.uid,
          email: profile.email || user.email || "",
          displayName: profile.displayName,
          loginName: profile.loginName || user.email?.split("@")[0] || "",
        });
        clearPermissionErrorFor("ensure-profile");
      } catch (err) {
        console.error("[chat] ensureProfile failed:", err);
        const softBootstrapProfileFailure = isQuotaExceeded(err)
          || /timed out|throttled/i.test(String(err?.message || ""));
        if (!softBootstrapProfileFailure && shouldSurfaceFirestoreError(err)) {
          setPermissionErrorFor("ensure-profile", err);
        }
        // Keep fallback profile so the shell stays interactive.
        setMe(fallbackMe);
      }
    });
  }, []);

  useEffect(() => {
    if (!me) return;
    localStorage.setItem("chat.status", statusMode);
    const pushVisibleStatus = () => setUserStatus(me.uid, statusMode).catch((err) => {
      if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
    });
    const pushInvisibleStatus = () => setUserStatus(me.uid, "invisible").catch(() => {});

    if (document.visibilityState === "visible") pushVisibleStatus();
    else pushInvisibleStatus();

    function onVisibilityChange() {
      if (document.visibilityState === "visible") {
        pushVisibleStatus();
      } else {
        pushInvisibleStatus();
      }
    }
    window.addEventListener("focus", pushVisibleStatus);
    window.addEventListener("blur", pushInvisibleStatus);
    window.addEventListener("pagehide", pushInvisibleStatus);
    window.addEventListener("beforeunload", pushInvisibleStatus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("focus", pushVisibleStatus);
      window.removeEventListener("blur", pushInvisibleStatus);
      window.removeEventListener("pagehide", pushInvisibleStatus);
      window.removeEventListener("beforeunload", pushInvisibleStatus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [me, statusMode]);

  useEffect(() => {
    localStorage.setItem("chat.settings", JSON.stringify(chatSettings));
  }, [chatSettings]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const smallViewportQuery = window.matchMedia(`(max-width: ${PHONE_UI_BREAKPOINT_PX}px)`);
    const touchOnlyQuery = window.matchMedia("(hover: none) and (pointer: coarse)");
    const update = () => {
      setIsPhoneUi(smallViewportQuery.matches || touchOnlyQuery.matches);
    };
    update();

    if (smallViewportQuery.addEventListener) {
      smallViewportQuery.addEventListener("change", update);
      touchOnlyQuery.addEventListener("change", update);
      return () => {
        smallViewportQuery.removeEventListener("change", update);
        touchOnlyQuery.removeEventListener("change", update);
      };
    }

    smallViewportQuery.addListener(update);
    touchOnlyQuery.addListener(update);
    return () => {
      smallViewportQuery.removeListener(update);
      touchOnlyQuery.removeListener(update);
    };
  }, []);

  useEffect(() => {
    if (!isPhoneUi) setPhoneSidebarOpen(false);
  }, [isPhoneUi]);

  // ── Live conversation list ───────────────────────────────────────────
  useEffect(() => {
    if (!me) return;
    const unsub = subscribeMyConversations(
      me.uid,
      (rows) => {
        const filteredRows = rows.filter((row) => !blockedConversationIds.includes(row.id));
        clearPermissionErrorFor("conversations-list");
        setConversations(filteredRows);
        setActiveId((cur) => {
          if (cur && (filteredRows.some((row) => row.id === cur) || serverConversationIdsRef.current.has(cur))) {
            return cur;
          }
          return (filteredRows[0] && filteredRows[0].id) || null;
        });
      },
      (err) => setPermissionErrorFor("conversations-list", err)
    );
    return unsub;
  }, [blockedConversationIds, me]);

  useEffect(() => {
    if (!me) return;
    return subscribeConversationPrefs(
      me.uid,
      (prefs) => {
        clearPermissionErrorFor("conversation-prefs");
        setConversationPrefsById(prefs);
      },
      (err) => shouldSurfaceFirestoreError(err) && setPermissionErrorFor("conversation-prefs", err)
    );
  }, [me]);

  useEffect(() => {
    if (!me) {
      setServerConversations([]);
      return undefined;
    }
    return subscribeAnnouncementChannels(
      (rows) => {
        setServerConversations(rows);
        clearPermissionErrorFor("announcement-channels");
      },
      (err) => shouldSurfaceFirestoreError(err) && setPermissionErrorFor("announcement-channels", err)
    );
  }, [me]);

  useEffect(() => {
    if (!me) {
      setZkBackupStatus({ ...EMPTY_ZK_BACKUP_STATUS });
      setZkBackupStatusError("");
      return;
    }
    return subscribeCloudKeyBackupStatus(
      me.uid,
      (status) => {
        setZkBackupStatus(status || { ...EMPTY_ZK_BACKUP_STATUS });
        setZkBackupStatusError("");
        clearPermissionErrorFor("zk-backup-status");
      },
      (err) => {
        if (isQuotaExceeded(err)) {
          setZkBackupStatusError("Cloud backup status unavailable while quota is exceeded.");
          return;
        }
        if (shouldSurfaceFirestoreError(err)) setPermissionErrorFor("zk-backup-status", err);
        setZkBackupStatusError("Cloud backup status unavailable.");
      }
    );
  }, [me]);

  useEffect(() => {
    if (!me) {
      setShowModCongratsToast(false);
      return;
    }
    const profile = profilesByUid[me.uid];
    const untilMs = tsMs(profile?.modCongratsUntil);
    if (!untilMs || untilMs <= Date.now()) {
      setShowModCongratsToast(false);
      return;
    }
    setShowModCongratsToast(true);
    const id = setTimeout(() => setShowModCongratsToast(false), Math.max(250, untilMs - Date.now()));
    return () => clearTimeout(id);
  }, [me, profilesByUid]);

  useEffect(() => {
    if (!me) {
      setXpEvents([]);
      setXpToast(null);
      latestXpEventIdRef.current = "";
      xpFeedBootedRef.current = false;
      return;
    }
    return subscribeXpEvents(
      me.uid,
      (events) => {
        setXpEvents(events);
        clearPermissionErrorFor("xp-events");
        const latestEvent = events[0];
        if (!latestEvent?.id) {
          xpFeedBootedRef.current = true;
          latestXpEventIdRef.current = "";
          return;
        }
        if (!xpFeedBootedRef.current) {
          xpFeedBootedRef.current = true;
          latestXpEventIdRef.current = latestEvent.id;
          return;
        }
        if (latestEvent.id === latestXpEventIdRef.current) return;
        latestXpEventIdRef.current = latestEvent.id;
        setXpToast({
          id: latestEvent.id,
          text: describeXpEvent(latestEvent, profilesByUidRef.current),
        });
      },
      (err) => {
        if (shouldSurfaceFirestoreError(err)) setPermissionErrorFor("xp-events", err);
      }
    );
  }, [me]);

  useEffect(() => {
    if (!xpToast) return undefined;
    const id = setTimeout(() => setXpToast(null), 3600);
    return () => clearTimeout(id);
  }, [xpToast]);

  // ── Live user profiles / activity status ─────────────────────────────
  useEffect(() => {
    if (!me) return;
    return subscribeChatProfiles(
      (profiles) => {
        setProfilesByUid(profiles);
        if (me && profiles[me.uid]) clearPermissionErrorFor("ensure-profile");
        clearPermissionErrorFor("chat-profiles");
      },
      (err) => {
        console.error("[chat] profiles listener error:", err);
        if (shouldSurfaceFirestoreError(err)) setPermissionErrorFor("chat-profiles", err);
      }
    );
  }, [me]);

  useEffect(() => {
    profilesByUidRef.current = profilesByUid;
  }, [profilesByUid]);

  useEffect(() => {
    serverConversationIdsRef.current = new Set(serverConversations.map((conversation) => conversation.id));
  }, [serverConversations]);

  useEffect(() => {
    if (!me || !canPostAnnouncements) return;
    ensureAnnouncementsChannel({
      myUid: me.uid,
      profile: meProfileForEffects,
    }).catch((err) => {
      if (shouldSurfaceFirestoreError(err)) setPermissionErrorFor("ensure-announcements-channel", err);
    });
  }, [canPostAnnouncements, me, meProfileForEffects]);

  // ── Live messages for the active conversation ────────────────────────
  useEffect(() => {
    if (!activeId || !me) {
      setMessages([]);
      setTypingUsers([]);
      setReadReceipts({});
      setSearchOpen(false);
      setSearchQuery("");
      setSearchIndex(0);
      setReplyTo(null);
      setForwardMessage(null);
      setEditingMessage(null);
      return;
    }
    setMessages([]); // reset while we (re)subscribe
    const unsub = subscribeMessages(
      activeId,
      me.uid,
      (msgs) => {
        clearPermissionErrorFor("messages-list");
        setMessages(msgs);
      },
      (err) => {
        setPermissionErrorFor("messages-list", err);
        if (err && err.code === "permission-denied") {
          setBlockedConversationIds((prev) => (prev.includes(activeId) ? prev : [...prev, activeId]));
          setConversations((prev) => prev.filter((conversation) => conversation.id !== activeId));
          setActiveId((current) => (current === activeId ? null : current));
        }
      }
    );
    return unsub;
  }, [activeId, me]);

  useEffect(() => {
    if (!editingMessage || !me) return;
    const latest = messages.find((message) => message.id === editingMessage.id);
    if (!latest || latest.deletedForEveryoneAt || latest.hiddenFor.includes(me.uid)) {
      setEditingMessage(null);
    }
  }, [editingMessage, me, messages]);

  useEffect(() => {
    if (!activeId || !me) {
      setTypingUsers([]);
      return;
    }
    const activeServerConversation = serverConversations.find((conversation) => conversation.id === activeId);
    if (activeServerConversation && isAnnouncementChannelConversation(activeServerConversation)) {
      setTypingUsers([]);
      return;
    }
    return subscribeTyping(
      activeId,
      setTypingUsers,
      (err) => shouldSurfaceFirestoreError(err) && setPermissionError(err)
    );
  }, [activeId, me, serverConversations]);

  useEffect(() => {
    if (typingUsers.length === 0) return;
    const id = setInterval(() => {
      setTypingUsers((rows) =>
        rows.filter((row) => {
          const ms = tsMs(row.updatedAt);
          return ms && Date.now() - ms < 6000;
        })
      );
    }, 1000);
    return () => clearInterval(id);
  }, [typingUsers.length]);

  useEffect(() => {
    if (!activeId || !me) {
      setReadReceipts({});
      return;
    }
    const activeServerConversation = serverConversations.find((conversation) => conversation.id === activeId);
    if (activeServerConversation && isAnnouncementChannelConversation(activeServerConversation)) {
      setReadReceipts({});
      return;
    }
    return subscribeReadReceipts(
      activeId,
      setReadReceipts,
      (err) => shouldSurfaceFirestoreError(err) && setPermissionError(err)
    );
  }, [activeId, me, serverConversations]);

  const latestMessageId = messages[messages.length - 1]?.id || "";

  useEffect(() => {
    if (!activeId || !me || messages.length === 0) return;
    const activeServerConversation = serverConversations.find((conversation) => conversation.id === activeId);
    if (!(activeServerConversation && isAnnouncementChannelConversation(activeServerConversation))) {
      markConversationRead(activeId, me.uid).catch((err) => {
        if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
      });
    }
    setConversationLastRead({ myUid: me.uid, convId: activeId }).catch((err) => {
      if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
    });
  }, [activeId, me, messages.length, latestMessageId, serverConversations]);

  const homeConversations = useMemo(
    () => conversations.filter((conversation) => !isAnnouncementChannelConversation(conversation)),
    [conversations]
  );
  const allConversations = useMemo(() => {
    const map = new Map();
    homeConversations.forEach((conversation) => {
      map.set(conversation.id, conversation);
    });
    serverConversations.forEach((conversation) => {
      map.set(conversation.id, conversation);
    });
    return Array.from(map.values());
  }, [homeConversations, serverConversations]);
  const activeConv = useMemo(
    () => allConversations.find((c) => c.id === activeId) || null,
    [allConversations, activeId]
  );
  const meUid = me?.uid || "";
  const activeConvId = activeConv?.id || "";
  const activeConvType = activeConv?.type || "";
  const activeConvMemberIds = useMemo(
    () => (Array.isArray(activeConv?.memberIds) ? activeConv.memberIds : []),
    [activeConv]
  );
  const activeConvMemberKey = activeConv
    ? activeConvMemberIds.join(":")
    : "";
  const activeIsAnnouncementChannel = !!activeConv && isAnnouncementChannelConversation(activeConv);
  const activeAnnouncementsReadOnly = activeIsAnnouncementChannel && !canPostAnnouncements;

  const serverTabs = useMemo(() => {
    const map = new Map();
    serverConversations.forEach((conversation) => {
      if (!conversation.serverId) return;
      if (!map.has(conversation.serverId)) {
        map.set(conversation.serverId, {
          id: conversation.serverId,
          name: conversation.serverName || ANNOUNCEMENTS_SERVER_NAME,
        });
      }
    });
    return Array.from(map.values());
  }, [serverConversations]);

  useEffect(() => {
    if (activeServerId === HOME_SERVER_TAB_ID) return;
    if (!serverTabs.some((server) => server.id === activeServerId)) {
      setActiveServerId(HOME_SERVER_TAB_ID);
      return;
    }
    const channels = serverConversations.filter((conversation) => conversation.serverId === activeServerId);
    if (channels.length > 0 && !channels.some((conversation) => conversation.id === activeId)) {
      setActiveId(channels[0].id);
    }
  }, [activeId, activeServerId, serverConversations, serverTabs]);

  useEffect(() => {
    if (!activeId) return;
    const matchingServerConversation = serverConversations.find((conversation) => conversation.id === activeId);
    if (matchingServerConversation?.serverId) {
      if (activeServerId !== matchingServerConversation.serverId) {
        setActiveServerId(matchingServerConversation.serverId);
      }
      return;
    }
    if (homeConversations.some((conversation) => conversation.id === activeId) && activeServerId !== HOME_SERVER_TAB_ID) {
      setActiveServerId(HOME_SERVER_TAB_ID);
    }
  }, [activeId, activeServerId, homeConversations, serverConversations]);

  useEffect(() => {
    if (!activeConvId || !meUid || activeConvType !== "group") return;
    warmConversationSendCache({
      convId: activeConvId,
      myUid: meUid,
      memberIds: activeConvMemberIds,
      publicKeysByUid: profilesByUidRef.current,
    }).catch(() => {});
  }, [activeConvId, activeConvMemberIds, activeConvMemberKey, activeConvType, meUid]);

  const forwardTargets = useMemo(() => {
    if (!me) return [];
    return allConversations
      .filter((conversation) => !isAnnouncementChannelConversation(conversation) || canPostAnnouncements)
      .map((conversation) => {
      const title = conversationLabel(conversation, me.uid, profilesByUid);
      const colorKey = conversation.type === "group"
        ? "group:" + conversation.id
        : (conversation.memberIds.find((id) => id !== me.uid) || conversation.id);
      return {
        id: conversation.id,
        title,
        subtitle: conversation.type === "group"
          ? `${conversation.memberIds.length} members`
          : directStatusLabel(conversation, me.uid, profilesByUid),
        avatarText: avatarInitial(title),
        avatarBg: avatarColor(colorKey),
      };
    });
  }, [allConversations, canPostAnnouncements, me, profilesByUid]);
  const conversationGroups = useMemo(() => {
    const inbox = [];
    const archived = [];
    const hidden = [];
    const source = activeServerId === HOME_SERVER_TAB_ID
      ? homeConversations
      : serverConversations.filter((conversation) => conversation.serverId === activeServerId);
    source.forEach((conversation) => {
      if (activeServerId !== HOME_SERVER_TAB_ID) {
        inbox.push(conversation);
        return;
      }
      const pref = conversationPrefsById[conversation.id] || {};
      if (pref.hidden) {
        hidden.push(conversation);
      } else if (pref.archived) {
        archived.push(conversation);
      } else {
        inbox.push(conversation);
      }
    });
    return { inbox, archived, hidden };
  }, [activeServerId, conversationPrefsById, homeConversations, serverConversations]);
  const visibleMessages = useMemo(() => {
    if (!me) return messages;
    return messages.filter((message) => {
      if (message.hiddenFor.includes(me.uid)) return false;
      // A banned person's messages disappear for everyone (reversible on unban).
      // Your own messages are never hidden by ban state.
      if (message.senderId && message.senderId !== me.uid) {
        const senderProfile = profilesByUid[message.senderId];
        if (senderProfile && isChatAccessDisabledProfile(senderProfile)) return false;
      }
      return true;
    });
  }, [me, messages, profilesByUid]);
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const searchResultIds = useMemo(() => {
    if (!normalizedSearchQuery) return [];
    return visibleMessages
      .filter((message) => messageSearchText(message).includes(normalizedSearchQuery))
      .map((message) => message.id);
  }, [normalizedSearchQuery, visibleMessages]);
  const activeSearchMessageId = searchResultIds[searchIndex] || "";

  useEffect(() => {
    setSearchIndex(0);
  }, [normalizedSearchQuery, activeId]);

  // ── Handlers ─────────────────────────────────────────────────────────
  async function handleSend({ body, replyTo: reply, imageFile }) {
    if (!activeConv || !me) return;
    if (activeAnnouncementsReadOnly) {
      throw new Error("Only helper mods, respected moderators, and Admin Vivaan can post in announcements.");
    }
    if (meTimedOut) throw new Error(timeoutMessage || "You are temporarily timed out.");
    try {
      if (editingMessage) {
        await editMessage({
          convId: activeConv.id,
          message: editingMessage,
          myUid: me.uid,
          body,
          publicKeysByUid: profilesByUid,
        });
        setEditingMessage(null);
        handleTyping(false);
        return;
      }

      await sendMessage({
        convId: activeConv.id,
        myUid: me.uid,
        body,
        imageFile,
        memberIds: activeConv.memberIds,
        replyTo: reply,
        publicKeysByUid: profilesByUid,
      });
      setReplyTo(null);
      handleTyping(false);
    } catch (err) {
      if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
      throw err; // let MessageInput restore the text and show its own alert
    }
  }

  function handleReply(message) {
    setEditingMessage(null);
    setForwardMessage(null);
    setReplyTo(message);
  }

  function handleEdit(message) {
    setReplyTo(null);
    setForwardMessage(null);
    setEditingMessage(message);
  }

  function handleForward(message) {
    setReplyTo(null);
    setEditingMessage(null);
    setForwardMessage(message);
  }

  function handleSearchToggle() {
    setSearchOpen((open) => {
      if (open) {
        setSearchQuery("");
        setSearchIndex(0);
      }
      return !open;
    });
  }

  function handleSearchStep(direction) {
    if (searchResultIds.length === 0) return;
    setSearchIndex((current) => {
      const next = current + direction;
      if (next < 0) return searchResultIds.length - 1;
      if (next >= searchResultIds.length) return 0;
      return next;
    });
  }

  async function handleDelete(message, scope) {
    if (!activeConv || !me) return;
    if (meTimedOut) return;
    try {
      if (scope === "everyone") {
        await deleteMessageForEveryone({
          convId: activeConv.id,
          message,
          myUid: me.uid,
        });
      } else {
        await hideMessageForMe({
          convId: activeConv.id,
          messageId: message.id,
          myUid: me.uid,
        });
      }
      if (editingMessage && editingMessage.id === message.id) setEditingMessage(null);
      if (replyTo && replyTo.id === message.id && scope === "everyone") setReplyTo(null);
    } catch (err) {
      if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
    }
  }

  async function handleTimeoutUser(targetUid, durationMs) {
    if (!showAdminModeration || !me || !targetUid || targetUid === me.uid) return;
    const currentMeProfile = { ...me, ...(profilesByUid[me.uid] || {}), status: statusMode };
    const actingAsAdmin = isChatAdminIdentity(currentMeProfile);
    const actingAsModerator = !actingAsAdmin && isModeratorProfile(currentMeProfile);
    const moderationLimits = moderationLimitsForProfile(currentMeProfile);
    if (actingAsModerator && !canUseModeratorAction(currentMeProfile, "timeout")) {
      alert(`${moderationRoleLabel(currentMeProfile)} can timeout up to ${moderationLimits.timeoutLimit} people per day.`);
      return;
    }
    // Moderation hierarchy: a moderator cannot shorten/overwrite a timeout that
    // Admin Vivaan applied. Firestore rules enforce this too.
    if (actingAsModerator) {
      const targetProfile = profilesByUid[targetUid] || {};
      const setterUid = targetProfile.timedOutBy || "";
      const setByAdmin = !!setterUid
        && isProfileTimedOut(targetProfile)
        && isChatAdminIdentity(profilesByUid[setterUid] || {});
      if (setByAdmin) {
        alert("Only Admin Vivaan can change a timeout set by Admin Vivaan.");
        return;
      }
    }
    try {
      await setChatTimeout({ targetUid, durationMs, myUid: me.uid });
      if (actingAsModerator) {
        await consumeModeratorActionQuota({
          uid: me.uid,
          profile: currentMeProfile,
          actionType: "timeout",
        });
      }
    } catch (err) {
      if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
      alert(err?.message || "Couldn't apply timeout.");
    }
  }

  async function handleClearTimeout(targetUid) {
    if (!showAdminModeration || !targetUid) return;
    // Moderation hierarchy: a timeout applied by Admin Vivaan can only be cleared by
    // Admin Vivaan. Firestore rules enforce this too; this is the friendly UI guard.
    const currentMeProfile = { ...me, ...(profilesByUid[me.uid] || {}), status: statusMode };
    const iAmAdmin = isChatAdminIdentity(currentMeProfile);
    const targetProfile = profilesByUid[targetUid] || {};
    const setterUid = targetProfile.timedOutBy || "";
    const setByActiveAdmin = !!setterUid
      && isProfileTimedOut(targetProfile)
      && isChatAdminIdentity(profilesByUid[setterUid] || {});
    if (setByActiveAdmin && !iAmAdmin) {
      alert("Only Admin Vivaan can clear a timeout set by Admin Vivaan.");
      return;
    }
    try {
      await clearChatTimeout({ targetUid });
    } catch (err) {
      if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
      alert(err?.message || "Couldn't clear that timeout.");
    }
  }

  function handleTyping(isTyping) {
    if (!activeConv || !me) return;
    if (activeIsAnnouncementChannel) return;
    const now = Date.now();
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    if (!isTyping) {
      setTyping(activeConv.id, me.uid, false).catch(() => {});
      return;
    }
    if (now - lastTypingSentRef.current > 1200) {
      lastTypingSentRef.current = now;
      setTyping(activeConv.id, me.uid, true).catch(() => {});
    }
    typingTimerRef.current = setTimeout(() => {
      setTyping(activeConv.id, me.uid, false).catch(() => {});
    }, 5000);
  }

  async function handleReaction(message, emoji) {
    if (!activeConv || !me) return;
    if (meTimedOut) return;
    try {
      await toggleReaction({
        convId: activeConv.id,
        messageId: message.id,
        myUid: me.uid,
        emoji,
        currentReactions: message.reactions,
      });
    } catch (err) {
      if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
    }
  }

  async function handleForwardSubmit(targetConvId) {
    if (!forwardMessage || !activeConv || !me) return;
    const targetConversation = conversations.find((conversation) => conversation.id === targetConvId);
    if (!targetConversation) throw new Error("Pick a valid chat to forward into.");

    try {
      await sendMessage({
        convId: targetConversation.id,
        myUid: me.uid,
        body: forwardMessage.body,
        memberIds: targetConversation.memberIds,
        publicKeysByUid: profilesByUid,
        forwardedFrom: {
          senderId: forwardMessage.senderId,
          conversationId: activeConv.id,
          conversationName: conversationLabel(activeConv, me.uid, profilesByUid),
        },
      });
      setActiveId(targetConversation.id);
      setForwardMessage(null);
    } catch (err) {
      if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
      throw err;
    }
  }

  async function handleNewChat(payload) {
    if (!me) return;
    if (meTimedOut) return;
    try {
      let convId;
      if (payload.type === "dm") {
        const conv = await getOrCreateDm(me.uid, payload.otherUid);
        convId = conv.id;
      } else if (payload.type === "add-people") {
        if (!addPeopleTarget) return;
        const conv = await addPeopleToGroup({
          convId: addPeopleTarget.id,
          myUid: me.uid,
          name: payload.name,
          memberIds: payload.memberIds,
        });
        convId = conv.id;
      } else {
        const conv = await createGroup({ myUid: me.uid, name: payload.name, memberIds: payload.memberIds });
        convId = conv.id;
      }
      setActiveId(convId);
      setShowNew(false);
      setAddPeopleTarget(null);
    } catch (err) {
      if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
      throw err;
    }
  }

  async function handleConversationArchive(convId, archived) {
    if (!me || !convId) return;
    try {
      await setConversationArchived({ myUid: me.uid, convId, archived });
    } catch (err) {
      if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
    }
  }

  async function handleConversationHide(convId, hidden) {
    if (!me || !convId) return;
    try {
      await setConversationHidden({ myUid: me.uid, convId, hidden });
    } catch (err) {
      if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
    }
  }

  async function handleRemoveMember(conversation, targetUid) {
    if (!me || !conversation?.id || !targetUid) return;
    const currentMeProfile = { ...me, ...(profilesByUid[me.uid] || {}), status: statusMode };
    const actingAsAdmin = isChatAdminIdentity(currentMeProfile);
    const actingAsModerator = !actingAsAdmin && isModeratorProfile(currentMeProfile);
    const moderationLimits = moderationLimitsForProfile(currentMeProfile);
    if (actingAsModerator && !canUseModeratorAction(currentMeProfile, "remove")) {
      alert(`${moderationRoleLabel(currentMeProfile)} can remove up to ${moderationLimits.removeLimit} people per day.`);
      return;
    }
    try {
      await removeGroupMember({
        convId: conversation.id,
        myUid: me.uid,
        targetUid,
      });
      if (actingAsModerator) {
        await consumeModeratorActionQuota({
          uid: me.uid,
          profile: currentMeProfile,
          actionType: "remove",
        });
      }
    } catch (err) {
      if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
      alert(err?.message || "Couldn't remove that person.");
    }
  }

  async function handleSetMemberRole(targetUid, role) {
    if (!me || !targetUid) return;
    const currentMeProfile = { ...me, ...(profilesByUid[me.uid] || {}), status: statusMode };
    if (!isChatAdminIdentity(currentMeProfile)) {
      alert("Only Admin Vivaan can manage roles.");
      return;
    }
    try {
      await setMemberModerationRole({
        targetUid,
        moderationRole: role,
        myUid: me.uid,
      });
    } catch (err) {
      if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
      alert(err?.message || "Couldn't update member role.");
    }
  }

  async function handleSetClownRole(targetUid, enabled) {
    if (!me || !targetUid) return;
    const currentMeProfile = { ...me, ...(profilesByUid[me.uid] || {}), status: statusMode };
    if (!isChatAdminIdentity(currentMeProfile)) {
      alert("Only Admin Vivaan can assign clown role.");
      return;
    }
    try {
      await setClownRole({
        targetUid,
        enabled,
        myUid: me.uid,
      });
    } catch (err) {
      if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
      alert(err?.message || "Couldn't update clown role.");
    }
  }

  async function handleAdjustXpPoints(action) {
    const { targetUid, amount, mode, reason } = action || {};
    if (!me || !targetUid) return null;
    const currentMeProfile = { ...me, ...(profilesByUid[me.uid] || {}), status: statusMode };
    if (!isChatAdminIdentity(currentMeProfile)) {
      throw new Error("Only Admin Vivaan can give XP points.");
    }
    try {
      return await adjustXpPoints({
        targetUid,
        amount,
        mode,
        reason,
        myUid: me.uid,
      });
    } catch (err) {
      if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
      throw err;
    }
  }

  async function handleRemoveComputerPermanently(targetUid) {
    if (!me || !targetUid) return;
    const currentMeProfile = { ...me, ...(profilesByUid[me.uid] || {}), status: statusMode };
    if (!isChatAdminIdentity(currentMeProfile)) {
      alert("Only Admin Vivaan can permanently remove the physical device from the website.");
      return;
    }
    const targetProfile = profilesByUid[targetUid] || {};
    if (!isComputerProfile(targetProfile)) {
      alert("This permanent action is reserved for the physical device account.");
      return;
    }

    const confirmed = window.confirm(
      "Permanently remove the physical device from the WHOLE website?\n\n"
      + "This kicks the physical device out of every group, deletes the chat profile, and "
      + "blocks the account from coming back. You can re-do this any time if it returns."
    );
    if (!confirmed) return;

    try {
      const result = await removeComputerFromWebsite({
        targetUid,
        myUid: me.uid,
      });
      const groupsUpdated = Number(result?.groupsUpdated || 0);
      const profileDeleted = !!result?.profileDeleted;
      const parts = [];
      if (profileDeleted) parts.push("physical device chat profile deleted");
      if (groupsUpdated > 0) parts.push(`removed from ${groupsUpdated} group${groupsUpdated === 1 ? "" : "s"}`);
      parts.push("future sign-ins blocked");
      alert(`Physical device was wiped from the website (${parts.join(", ")}).`);
    } catch (err) {
      if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
      alert(err?.message || "Couldn't permanently remove the physical device from the website.");
    }
  }

  async function handleDeleteAccountPermanently(targetUid) {
    if (!me || !targetUid) return;
    const currentMeProfile = { ...me, ...(profilesByUid[me.uid] || {}), status: statusMode };
    if (!isChatAdminIdentity(currentMeProfile)) {
      alert("Only Admin Vivaan can permanently delete accounts.");
      return;
    }
    const result = await deleteChatAccountPermanently({ targetUid });
    if (activeConv && activeConv.type !== "group" && (activeConv.memberIds || []).includes(targetUid)) {
      setActiveId(null);
    }
    return result;
  }

  // Ban / unban a person from the chat entirely. Admin Vivaan only. A ban blocks
  // every write from that account (enforced by Firestore rules) and hides every
  // message they ever sent from everyone. Reversible via unban.
  async function handleSetChatBan(targetUid, disabled) {
    if (!me || !targetUid) return;
    const currentMeProfile = { ...me, ...(profilesByUid[me.uid] || {}), status: statusMode };
    if (!isChatAdminIdentity(currentMeProfile)) {
      alert("Only Admin Vivaan can ban or unban people.");
      return;
    }
    try {
      await setChatAccessDisabled({ targetUid, disabled, myUid: me.uid });
      if (disabled
          && activeConv
          && activeConv.type !== "group"
          && (activeConv.memberIds || []).includes(targetUid)) {
        setActiveId(null);
      }
    } catch (err) {
      if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
      alert(err?.message || (disabled ? "Couldn't ban that person." : "Couldn't unban that person."));
    }
  }

  async function handleDeleteGroupPermanently(conversation) {
    if (!me || !conversation?.id) return;
    const currentMeProfile = { ...me, ...(profilesByUid[me.uid] || {}), status: statusMode };
    if (!isChatAdminIdentity(currentMeProfile)) {
      alert("Only Admin Vivaan can delete groups.");
      return;
    }
    const label = conversationLabel(conversation, me.uid, profilesByUid);
    const confirmed = window.confirm(
      `Permanently delete the group "${label}"?\n\n`
      + "This removes the group and ALL its messages for everyone. This CANNOT be undone."
    );
    if (!confirmed) return;
    try {
      await deleteChatGroupPermanently({ convId: conversation.id });
      if (activeId === conversation.id) setActiveId(null);
    } catch (err) {
      if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
      alert(err?.message || "Couldn't delete that group.");
    }
  }

  async function handleLeaveGroup(conversation) {
    if (!me || !conversation?.id) return;
    try {
      await leaveGroup({ convId: conversation.id, myUid: me.uid });
      if (activeId === conversation.id) {
        setActiveId(null);
      }
    } catch (err) {
      if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
      throw err;
    }
  }

  async function handleTransferOwnership(conversation, nextOwnerUid) {
    if (!conversation?.id || !nextOwnerUid) return;
    try {
      await transferGroupOwnership({
        convId: conversation.id,
        nextOwnerUid,
      });
    } catch (err) {
      if (shouldSurfaceFirestoreError(err)) setPermissionError(err);
      throw err;
    }
  }

  function handleAddPeople(conversation) {
    if (!conversation || conversation.type !== "group" || meTimedOut) return;
    setAddPeopleTarget(conversation);
    setShowNew(true);
  }

  async function handleSignOut() {
    if (me) await setUserStatus(me.uid, "invisible").catch(() => {});
    await signOutChat();
  }

  async function handleBackupKeys(passphrase) {
    if (!me) return;
    const backup = await backupChatKeysToCloud({
      uid: me.uid,
      passphrase,
    });
    const createdAt = Number(backup?.createdAt);
    setZkBackupStatus({
      exists: true,
      updatedAt: null,
      createdAt: Number.isFinite(createdAt) ? createdAt : Date.now(),
    });
    setZkBackupStatusError("");
    return backup;
  }

  async function handleRecoverKeys(passphrase) {
    if (!me) return;
    const result = await recoverChatKeysFromCloud({
      uid: me.uid,
      passphrase,
    });
    if (activeId) {
      const target = activeId;
      setActiveId(null);
      setTimeout(() => setActiveId(target), 0);
    }
    return result;
  }

  function updateChatSetting(key, value) {
    setChatSettings((prev) => ({ ...prev, [key]: value }));
  }

  // ── Render ───────────────────────────────────────────────────────────
  if (!authReady) {
    return <div className="chat-boot">Loading…</div>;
  }
  if (!me) {
    return <ChatLogin />;
  }
  const liveMe = {
    ...me,
    ...(profilesByUid[me.uid] || {}),
    status: statusMode,
  };
  if (liveMe.chatAccessDisabled) {
    return (
      <div className="chat-boot">
        Your chat access has been removed by Admin Vivaan.
      </div>
    );
  }
  const isAdmin = isChatAdminIdentity(liveMe);
  const isModerator = isModeratorProfile(liveMe);
  const showAdminModeration = isAdmin || isModerator;
  const moderationQuota = moderationQuotaForToday(liveMe);
  const moderationLimits = moderationLimitsForProfile(liveMe);
  const moderationHint = isAdmin
    ? ""
    : showAdminModeration
      ? `${moderationRoleLabel(liveMe)} limits today: ${Math.max(0, moderationLimits.timeoutLimit - moderationQuota.timeoutUsed)} timeouts left, ${Math.max(0, moderationLimits.removeLimit - moderationQuota.removeUsed)} removals left.`
      : "";
  const meTimedOut = isProfileTimedOut(liveMe);
  const timeoutMessage = meTimedOut ? `Timed out until ${formatTimeoutUntil(liveMe.timeoutUntil)}` : "";
  const visibleTypingUsers = typingUsers.filter((u) => u.uid !== me.uid);
  const typingText = formatTypingText(visibleTypingUsers, profilesByUid);
  const shellStyle = {
    "--gc-primary": chatSettings.accent,
    "--gc-primary-bg": hexToRgba(chatSettings.accent, 0.12),
  };
  const shellClasses = [
    "chat-shell",
    chatSettings.density === "compact" ? "compact" : "",
    `theme-${chatSettings.theme}`,
    isPhoneUi ? "phone-ui" : "",
    isPhoneUi && phoneSidebarOpen ? "phone-sidebar-open" : "",
  ].filter(Boolean).join(" ");

  if (secretAdminOnly && !isAdmin) {
    return (
      <div className="chat-boot">
        Admin Vivaan tools are only available to the admin account.
      </div>
    );
  }

  return (
    <div className={shellClasses} style={shellStyle}>
      <ChatRail
        me={liveMe}
        servers={serverTabs}
        activeServerId={activeServerId}
        onPickServer={(serverId) => {
          setActiveServerId(serverId);
          if (serverId === HOME_SERVER_TAB_ID) {
            if (activeConv && isAnnouncementChannelConversation(activeConv)) {
              setActiveId(homeConversations[0]?.id || null);
            }
            return;
          }
          const channels = serverConversations.filter((conversation) => conversation.serverId === serverId);
          if (channels.length > 0) {
            setActiveId(channels[0].id);
          }
        }}
        activePanel={activePanel}
        onTogglePanel={(panel) => setActivePanel((cur) => (cur === panel ? null : panel))}
      />
      <ChatSidebar
        me={liveMe}
        conversationGroups={conversationGroups}
        conversationPrefsById={conversationPrefsById}
        profilesByUid={profilesByUid}
        activeId={activeId}
        sectionLabel={activeServerId === HOME_SERVER_TAB_ID ? "Chats" : "Channels"}
        onPick={(id) => {
          const selectedConversation = allConversations.find((conversation) => conversation.id === id);
          setActiveServerId(selectedConversation?.serverId || HOME_SERVER_TAB_ID);
          setActiveId(id);
          if (isPhoneUi) setPhoneSidebarOpen(false);
        }}
        onNew={() => {
          setShowNew(true);
          if (isPhoneUi) setPhoneSidebarOpen(false);
        }}
        onAddPeople={handleAddPeople}
        onRemoveMember={handleRemoveMember}
        onLeaveGroup={handleLeaveGroup}
        onTransferOwnership={handleTransferOwnership}
        onArchiveConversation={handleConversationArchive}
        onHideConversation={handleConversationHide}
        onSignOut={handleSignOut}
        statusMode={statusMode}
        canAdminManageGroups={isAdmin}
        canModerateMembers={showAdminModeration}
        canAssignModerators={isAdmin}
        onSetMemberRole={handleSetMemberRole}
        onSetClownRole={handleSetClownRole}
        onStatusChange={setStatusMode}
        newChatDisabled={meTimedOut || activeServerId !== HOME_SERVER_TAB_ID}
        newChatTitle={activeServerId !== HOME_SERVER_TAB_ID
          ? "Server channels are managed separately."
          : (timeoutMessage || "New chat")}
        showCollapseControl={isPhoneUi}
        onCollapseSidebar={() => setPhoneSidebarOpen(false)}
      />
      {isPhoneUi && phoneSidebarOpen && (
        <button
          type="button"
          className="chat-mobile-sidebar-scrim"
          onClick={() => setPhoneSidebarOpen(false)}
          aria-label="Close chats panel"
        />
      )}
      {activePanel && (
        <ChatSidePanel
          panel={activePanel}
          me={liveMe}
          profilesByUid={profilesByUid}
          xpEvents={xpEvents}
          statusMode={statusMode}
          onStatusChange={setStatusMode}
          settings={chatSettings}
          onSettingChange={updateChatSetting}
          onSignOut={handleSignOut}
          onBackupKeys={handleBackupKeys}
          onRecoverKeys={handleRecoverKeys}
          zkBackupStatus={zkBackupStatus}
          zkBackupStatusError={zkBackupStatusError}
          onClose={() => setActivePanel(null)}
        />
      )}
      <main className="chat-main">
        {permissionError && (
          <PermissionBanner
            error={permissionError}
            onDismiss={() => setPermissionError(null)}
          />
        )}
        {activeConv ? (
          <>
            <header className="chat-header">
              <div className="chat-header-left">
                {isPhoneUi && (
                  <button
                    type="button"
                    className="chat-btn-icon chat-mobile-sidebar-toggle"
                    onClick={() => setPhoneSidebarOpen((open) => !open)}
                    title={phoneSidebarOpen ? "Hide chats" : "Show chats"}
                    aria-label={phoneSidebarOpen ? "Hide chats" : "Show chats"}
                  >
                    {phoneSidebarOpen ? "◀" : "▶"}
                  </button>
                )}
                {(() => {
                  const label = conversationLabel(activeConv, me.uid, profilesByUid);
                  const colorKey = activeConv.type === "group"
                    ? "group:" + activeConv.id
                    : (activeConv.memberIds.find((id) => id !== me.uid) || activeConv.id);
                  return (
                    <div
                      className="chat-avatar lg"
                      style={{ background: avatarColor(colorKey) }}
                      aria-hidden="true"
                    >
                      {avatarInitial(label)}
                    </div>
                  );
                })()}
                <div className="chat-header-text">
                  <div className="chat-title">
                    {activeIsAnnouncementChannel
                      ? `# ${conversationLabel(activeConv, me.uid, profilesByUid)}`
                      : conversationLabel(activeConv, me.uid, profilesByUid)}
                  </div>
                  <div className="chat-sub">
                    {activeIsAnnouncementChannel
                      ? `${activeConv.serverName || ANNOUNCEMENTS_SERVER_NAME} • everyone can read, only helper mods/mods/admin can post`
                      : activeConv.type === "group"
                      ? `${activeConv.memberIds.length} members`
                      : directStatusLabel(activeConv, me.uid, profilesByUid)}
                  </div>
                </div>
              </div>
              <div className="chat-header-actions">
                {isAdmin && (
                  <button
                    type="button"
                    className="chat-btn-icon"
                    onClick={() => setShowXpPointsModal(true)}
                    title="XP points admin"
                    aria-label="XP points admin"
                  >
                    ⭐
                  </button>
                )}
                {isAdmin && (
                  <button
                    type="button"
                    className="chat-btn-icon"
                    onClick={() => setShowModeratorPanel(true)}
                    title="Moderator admin"
                    aria-label="Moderator admin"
                  >
                    🛡
                  </button>
                )}
                {isAdmin && activeConv && activeConv.type === "group" && (
                  <button
                    type="button"
                    className="chat-btn-icon chat-btn-icon-danger"
                    onClick={() => handleDeleteGroupPermanently(activeConv)}
                    title="Delete this group permanently"
                    aria-label="Delete this group permanently"
                  >
                    🗑
                  </button>
                )}
                {searchOpen ? (
                  <div className="chat-header-search">
                    <input
                      autoFocus
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder="Search messages"
                      className="chat-header-search-input"
                    />
                    <span className="chat-search-count">
                      {normalizedSearchQuery
                        ? searchResultIds.length === 0
                          ? "0"
                          : `${searchIndex + 1}/${searchResultIds.length}`
                        : ""}
                    </span>
                    <button
                      type="button"
                      className="chat-btn-icon"
                      onClick={() => handleSearchStep(-1)}
                      disabled={searchResultIds.length === 0}
                      title="Previous result"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="chat-btn-icon"
                      onClick={() => handleSearchStep(1)}
                      disabled={searchResultIds.length === 0}
                      title="Next result"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="chat-btn-icon"
                      onClick={handleSearchToggle}
                      title="Close search"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="chat-btn-icon"
                    onClick={handleSearchToggle}
                    title="Search messages"
                    aria-label="Search messages"
                  >
                    🔎
                  </button>
                )}
              </div>
            </header>
            <ChatMessages
              messages={visibleMessages}
              meUid={me.uid}
              profilesByUid={profilesByUid}
              memberIds={activeConv.memberIds}
              typingIndicatorText={typingText}
              readReceipts={readReceipts}
              searchResultIds={searchResultIds}
              activeSearchMessageId={activeSearchMessageId}
              onReply={handleReply}
              onForward={handleForward}
              onReact={handleReaction}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onTimeoutUser={handleTimeoutUser}
              onClearTimeout={handleClearTimeout}
              canModerate={showAdminModeration}
              moderationQuota={moderationQuota}
              moderationLimits={moderationLimits}
              isAdmin={isAdmin}
              interactionDisabled={meTimedOut}
            />
            {typingText && <div className="chat-typing-line">{typingText}</div>}
            {meTimedOut && <div className="chat-timeout-line">{timeoutMessage}</div>}
            {activeAnnouncementsReadOnly && (
              <div className="chat-timeout-line">
                Only helper mods, respected moderators, and Admin Vivaan can post in announcements.
              </div>
            )}
            {!meTimedOut && moderationHint && <div className="chat-mod-limit-line">{moderationHint}</div>}
            <MessageInput
              onSend={handleSend}
              onTyping={handleTyping}
              meUid={me.uid}
              memberIds={activeConv.memberIds}
              editingMessage={editingMessage}
              onCancelEdit={() => setEditingMessage(null)}
              replyTo={replyTo}
              onCancelReply={() => setReplyTo(null)}
              profilesByUid={profilesByUid}
              disabled={meTimedOut || activeAnnouncementsReadOnly}
              placeholder={activeAnnouncementsReadOnly
                ? "Announcements are read only unless you are helper mod, respected moderator, or Admin Vivaan."
                : (timeoutMessage || undefined)}
            />
          </>
        ) : (
          <div className="chat-empty">
            {isPhoneUi && (
              <button
                type="button"
                className="chat-btn-link chat-empty-sidebar-toggle"
                onClick={() => setPhoneSidebarOpen((open) => !open)}
              >
                {phoneSidebarOpen ? "Hide chats" : "Show chats"}
              </button>
            )}
            <p>Pick a conversation, or start a new one.</p>
            <button className="chat-btn-primary" onClick={() => setShowNew(true)}>
              + New chat
            </button>
          </div>
        )}
      </main>

      {showNew && (
        <NewChatModal
          meUid={me.uid}
          onClose={() => {
            setShowNew(false);
            setAddPeopleTarget(null);
          }}
          onCreate={handleNewChat}
          mode={addPeopleTarget ? "add-people" : "new"}
          existingMemberIds={addPeopleTarget?.memberIds || []}
          title={addPeopleTarget ? "Add people" : "New chat"}
          description={addPeopleTarget
            ? "Pick people to add to this group."
            : undefined}
          submitLabel={addPeopleTarget ? "Add people" : undefined}
          defaultGroupName={addPeopleTarget
            ? addPeopleTarget.type === "group"
              ? addPeopleTarget.name || conversationLabel(addPeopleTarget, me.uid, profilesByUid)
              : `${conversationLabel(addPeopleTarget, me.uid, profilesByUid)} group`
            : ""}
        />
      )}
      {showModeratorPanel && isAdmin && me && (
        <ModeratorPanelModal
          meUid={me.uid}
          viewerIsAdmin={isAdmin}
          profilesByUid={profilesByUid}
          conversations={conversations}
          onSetMemberRole={handleSetMemberRole}
          onSetClownRole={handleSetClownRole}
          onSetChatBan={handleSetChatBan}
          onTimeoutUser={handleTimeoutUser}
          onClearTimeout={handleClearTimeout}
          onRemoveComputerPermanently={handleRemoveComputerPermanently}
          onKickFromGroup={handleRemoveMember}
          onDeleteAccountPermanently={handleDeleteAccountPermanently}
          onClose={() => setShowModeratorPanel(false)}
        />
      )}
      {showXpPointsModal && isAdmin && me && (
        <XpPointsModal
          me={liveMe}
          profilesByUid={profilesByUid}
          onAdjustXpPoints={handleAdjustXpPoints}
          onClose={() => setShowXpPointsModal(false)}
        />
      )}
      {forwardMessage && activeConv && me && (
        <ForwardMessageModal
          message={forwardMessage}
          sourceConversationLabel={conversationLabel(activeConv, me.uid, profilesByUid)}
          conversations={forwardTargets}
          activeConversationId={activeConv.id}
          senderLabel={forwardMessage.senderId === me.uid
            ? "You"
            : (profilesByUid[forwardMessage.senderId]?.displayName || "Someone")}
          previewText={describeMessagePreview(forwardMessage) || "Message"}
          onClose={() => setForwardMessage(null)}
          onForward={handleForwardSubmit}
        />
      )}
      {showModCongratsToast && (
        <div className="chat-mod-toast" role="status" aria-live="polite">
          congrats ur a mod
        </div>
      )}
      {xpToast && (
        <div className="chat-xp-toast" role="status" aria-live="polite">
          {xpToast.text}
        </div>
      )}
    </div>
  );
}

function ChatRail({ me, servers = [], activeServerId, onPickServer, activePanel, onTogglePanel }) {
  return (
    <aside className="chat-rail" aria-label="Chat controls">
      <div className="chat-rail-top">
        <div className="chat-rail-brand">VM</div>
        <div className="chat-rail-servers" aria-label="Servers">
          <button
            type="button"
            className={`chat-rail-btn ${activeServerId === HOME_SERVER_TAB_ID ? "active" : ""}`}
            onClick={() => onPickServer && onPickServer(HOME_SERVER_TAB_ID)}
            title="Home"
            aria-label="Home"
          >
            🏠
          </button>
          {servers.map((server) => (
            <button
              key={server.id}
              type="button"
              className={`chat-rail-btn ${activeServerId === server.id ? "active" : ""}`}
              onClick={() => onPickServer && onPickServer(server.id)}
              title={server.name}
              aria-label={server.name}
            >
              <span className="chat-rail-server-badge">
                {avatarInitial(server.name || "S")}
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className="chat-rail-bottom">
        <button
          type="button"
          className={`chat-rail-btn ${activePanel === "profile" ? "active" : ""}`}
          onClick={() => onTogglePanel("profile")}
          title="Profile"
          aria-label="Profile"
        >
          <span className="chat-rail-avatar" style={{ background: avatarColor(me.uid) }}>
            {avatarInitial(me.displayName)}
          </span>
        </button>
        <button
          type="button"
          className={`chat-rail-btn ${activePanel === "settings" ? "active" : ""}`}
          onClick={() => onTogglePanel("settings")}
          title="Settings"
          aria-label="Settings"
        >
          ⚙
        </button>
      </div>
    </aside>
  );
}

function ChatSidePanel({
  panel,
  me,
  profilesByUid = {},
  xpEvents = [],
  statusMode,
  onStatusChange,
  settings,
  onSettingChange,
  onSignOut,
  onBackupKeys,
  onRecoverKeys,
  zkBackupStatus,
  zkBackupStatusError,
  onClose,
}) {
  const isProfile = panel === "profile";
  const [recoveryPassphrase, setRecoveryPassphrase] = useState("");
  const [recoveryBusy, setRecoveryBusy] = useState("");
  const [recoveryNotice, setRecoveryNotice] = useState("");
  const backupSavedAtMs = tsMs(zkBackupStatus?.updatedAt) || Number(zkBackupStatus?.createdAt || 0);
  const backupSavedAtLabel = backupSavedAtMs
    ? new Date(backupSavedAtMs).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })
    : "";
  const backupStatusTone = zkBackupStatusError
    ? "warn"
    : zkBackupStatus?.exists
      ? "ok"
      : "missing";
  const backupStatusText = zkBackupStatusError
    ? "Status unavailable"
    : zkBackupStatus?.exists
      ? "Cloud backup saved"
      : "No cloud backup yet";
  const meXpLevel = xpLevelInfoForProfile(me);
  const meXpReward = xpRewardForProfile(me);
  const mergedProfilesByUid = useMemo(
    () => ({
      ...(profilesByUid || {}),
      [me.uid]: {
        ...(profilesByUid?.[me.uid] || {}),
        ...me,
      },
    }),
    [me, profilesByUid]
  );
  const leaderboardEntries = useMemo(() => {
    return Object.values(mergedProfilesByUid)
      .filter((profile) => profile?.uid && !profile.chatAccessDisabled)
      .sort((left, right) => {
        const pointsDelta = xpPointsForProfile(right) - xpPointsForProfile(left);
        if (pointsDelta !== 0) return pointsDelta;
        return String(left.displayName || "").localeCompare(String(right.displayName || ""));
      })
      .map((profile, index) => ({
        ...profile,
        rank: index + 1,
        xpPoints: xpPointsForProfile(profile),
        xpLevel: xpLevelInfoForProfile(profile),
        xpReward: xpRewardForProfile(profile),
      }));
  }, [mergedProfilesByUid]);
  const topLeaderboardEntries = leaderboardEntries.slice(0, 8);
  const myLeaderboardEntry = leaderboardEntries.find((entry) => entry.uid === me.uid) || null;

  function formatXpEventTime(event) {
    const ms = tsMs(event?.createdAt);
    if (!ms) return "Just now";
    return new Date(ms).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  function formatXpEventDelta(event) {
    if (event?.type === "set") return `Set to ${event?.nextXpPoints || 0} XP`;
    const delta = Number(event?.amount || 0);
    return `${delta > 0 ? "+" : ""}${delta} XP`;
  }

  async function runBackup() {
    if (!onBackupKeys) return;
    if (!recoveryPassphrase.trim()) {
      setRecoveryNotice("Enter a passphrase first.");
      return;
    }
    setRecoveryBusy("backup");
    setRecoveryNotice("");
    try {
      await onBackupKeys(recoveryPassphrase);
      setRecoveryNotice("Backup saved.");
    } catch (err) {
      setRecoveryNotice(err?.message || "Backup failed.");
    } finally {
      setRecoveryBusy("");
    }
  }

  async function runRecover() {
    if (!onRecoverKeys) return;
    if (!recoveryPassphrase.trim()) {
      setRecoveryNotice("Enter your recovery passphrase.");
      return;
    }
    setRecoveryBusy("recover");
    setRecoveryNotice("");
    try {
      const result = await onRecoverKeys(recoveryPassphrase);
      const count = Number(result?.restoredSenderKeyCount || 0);
      setRecoveryNotice(`Recovered keys (${count} sender keys).`);
    } catch (err) {
      setRecoveryNotice(err?.message || "Recovery failed.");
    } finally {
      setRecoveryBusy("");
    }
  }

  return (
    <aside className="chat-side-panel">
      <div className="chat-side-panel-head">
        <h2>{isProfile ? "Profile" : "Settings"}</h2>
        <button type="button" className="chat-btn-icon" onClick={onClose} title="Close">✕</button>
      </div>

      {isProfile ? (
        <>
          <div className="chat-profile-card">
            <div className="chat-profile-avatar" style={{ background: avatarColor(me.uid) }}>
              {avatarInitial(me.displayName)}
            </div>
            <h3>{me.displayName}</h3>
            <p>{me.email || "No email saved"}</p>
            <span>@{me.loginName || "chat"}</span>
            <div className="chat-profile-xp-summary">
              <div className="chat-profile-xp-line">
                <strong>{meXpLevel.xpPoints} XP</strong>
                <span className={`chat-xp-badge ${meXpReward.key}`}>L{meXpLevel.level} {meXpReward.label}</span>
              </div>
              <div className="chat-xp-progress" aria-hidden="true">
                <div
                  className={`chat-xp-progress-bar ${meXpReward.key}`}
                  style={{ width: `${Math.max(6, meXpLevel.progress * 100)}%` }}
                />
              </div>
              <span className="chat-profile-mini-note">
                {meXpLevel.pointsNeeded} XP to level {meXpLevel.level + 1}
              </span>
              <span className="chat-profile-mini-note">
                {myLeaderboardEntry
                  ? `Leaderboard rank #${myLeaderboardEntry.rank} of ${leaderboardEntries.length}`
                  : "Leaderboard rank unavailable"}
              </span>
            </div>
          </div>
          <div className="chat-xp-panel-card">
            <strong>XP history</strong>
            <span>Recent rewards, removals, and corrections on your account.</span>
            {xpEvents.length === 0 ? (
              <div className="chat-xp-empty">No XP activity yet.</div>
            ) : (
              xpEvents.slice(0, 6).map((event) => (
                <div key={event.id} className="chat-xp-history-row">
                  <div className="chat-xp-history-top">
                    <strong>{formatXpEventDelta(event)}</strong>
                    <span>{formatXpEventTime(event)}</span>
                  </div>
                  <div className="chat-xp-history-copy">{describeXpEvent(event, mergedProfilesByUid)}</div>
                </div>
              ))
            )}
          </div>
          <div className="chat-xp-panel-card">
            <strong>Leaderboard</strong>
            <span>Top XP earners across live chat.</span>
            {topLeaderboardEntries.length === 0 ? (
              <div className="chat-xp-empty">No leaderboard data yet.</div>
            ) : (
              topLeaderboardEntries.map((entry) => (
                <div
                  key={entry.uid}
                  className={"chat-xp-leaderboard-row" + (entry.uid === me.uid ? " is-me" : "")}
                >
                  <span className="chat-xp-rank-pill">#{entry.rank}</span>
                  <div className="chat-xp-leaderboard-meta">
                    <strong>{entry.displayName || "User"}{entry.uid === me.uid ? " (you)" : ""}</strong>
                    <span>{entry.xpPoints} XP • Level {entry.xpLevel.level}</span>
                  </div>
                  <span className={`chat-xp-badge ${entry.xpReward.key}`}>{entry.xpReward.label}</span>
                </div>
              ))
            )}
          </div>
          <label className="chat-setting-row">
            <span>Activity mode</span>
            <select value={statusMode} onChange={(e) => onStatusChange(e.target.value)}>
              {Object.entries(CHAT_STATUSES).map(([value, cfg]) => (
                <option key={value} value={value}>{cfg.label}</option>
              ))}
            </select>
          </label>
          <div className="chat-zk-card">
            <strong>Zero-knowledge recovery</strong>
            <p>Backup or recover local chat keys with your passphrase.</p>
            <span className={`chat-zk-status ${backupStatusTone}`} role="status" aria-live="polite">
              {backupStatusText}
            </span>
            {backupSavedAtLabel && !zkBackupStatusError && (
              <span className="chat-zk-card-note">Last backup: {backupSavedAtLabel}</span>
            )}
            {zkBackupStatusError && <span className="chat-zk-card-note">{zkBackupStatusError}</span>}
            <input
              type="password"
              value={recoveryPassphrase}
              onChange={(event) => setRecoveryPassphrase(event.target.value)}
              placeholder="Recovery passphrase"
              className="chat-modal-search"
            />
            <div className="chat-zk-card-actions">
              <button
                type="button"
                className="chat-msg-action-btn"
                onClick={runBackup}
                disabled={!!recoveryBusy}
              >
                {recoveryBusy === "backup" ? "Saving..." : "Backup keys"}
              </button>
              <button
                type="button"
                className="chat-msg-action-btn"
                onClick={runRecover}
                disabled={!!recoveryBusy}
              >
                {recoveryBusy === "recover" ? "Recovering..." : "Recover keys"}
              </button>
            </div>
            {recoveryNotice && <span className="chat-zk-card-note">{recoveryNotice}</span>}
          </div>
          <button type="button" className="chat-btn-primary chat-panel-wide-btn" onClick={onSignOut}>
            Sign out
          </button>
        </>
      ) : (
        <>
          <label className="chat-setting-row">
            <span>Accent color</span>
            <select value={settings.accent} onChange={(e) => onSettingChange("accent", e.target.value)}>
              <option value="#8ab4f8">Blue</option>
              <option value="#81c995">Green</option>
              <option value="#fdd663">Gold</option>
              <option value="#f28b82">Rose</option>
              <option value="#c58af9">Violet</option>
            </select>
          </label>
          <label className="chat-setting-row">
            <span>Message spacing</span>
            <select value={settings.density} onChange={(e) => onSettingChange("density", e.target.value)}>
              <option value="comfortable">Comfortable</option>
              <option value="compact">Compact</option>
            </select>
          </label>
          <label className="chat-setting-row">
            <span>Mode</span>
            <select value={settings.theme} onChange={(e) => onSettingChange("theme", e.target.value)}>
              <option value="dark">Dark</option>
              <option value="contrast">High contrast</option>
            </select>
          </label>
        </>
      )}
    </aside>
  );
}

function conversationLabel(c, meUid, profilesByUid) {
  if (c.type === "group") return c.name || "Untitled group";
  const otherUid = c.memberIds.find((id) => id !== meUid);
  const other = otherUid ? profilesByUid[otherUid] : null;
  return other ? other.displayName : "Direct message";
}

function isQuotaExceeded(err) {
  if (!err) return false;
  return err.code === "resource-exhausted" || /quota exceeded|resource exhausted/i.test(err.message || "");
}

function shouldSurfaceFirestoreError(err) {
  if (!err) return false;
  return (
    err.code === "permission-denied" ||
    /insufficient permissions/i.test(err.message || "") ||
    isQuotaExceeded(err)
  );
}

function directStatusLabel(c, meUid, profilesByUid) {
  const otherUid = c.memberIds.find((id) => id !== meUid);
  const profile = otherUid ? profilesByUid[otherUid] : null;
  if (!profile) return "Direct message";
  const status = effectiveStatus(profile);
  const label = status === "invisible" ? "Offline" : CHAT_STATUSES[status]?.label || "Offline";
  return label;
}

function effectiveStatus(profile) {
  if (!profile) return "invisible";
  return CHAT_STATUSES[profile.status] ? profile.status : "invisible";
}

function formatTypingText(rows, profilesByUid) {
  if (!rows.length) return "";
  const names = rows.map((r) => profilesByUid[r.uid]?.displayName || "Someone");
  if (names.length === 1) return `${names[0]} is typing...`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing...`;
  return "Several people are typing...";
}

function formatTimeoutUntil(timeoutUntil) {
  const ms = tsMs(timeoutUntil);
  if (!ms) return "later";
  return new Date(ms).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function messageSearchText(message) {
  return [
    message.body,
    message.replyTo?.body,
    message.forwardedFrom?.conversationName,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function hexToRgba(hex, alpha) {
  const clean = String(hex || "").replace("#", "");
  if (clean.length !== 6) return `rgba(138, 180, 248, ${alpha})`;
  const value = parseInt(clean, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function PermissionBanner({ error, onDismiss }) {
  const code = error && error.code ? String(error.code) : "";
  const msg = error && error.message ? String(error.message) : String(error || "");
  const where = error && error.where ? String(error.where) : "";
  const uidUsed = error && error.uidUsed ? String(error.uidUsed) : "";
  const convId = error && error.convId ? String(error.convId) : "";
  const isIndexPending = code === "failed-precondition" && /index/i.test(msg);
  const quotaExceeded = isQuotaExceeded(error);
  return (
    <div className="chat-perm-banner">
      <div className="chat-perm-banner-body">
        <strong>Couldn&apos;t sync with Firestore.</strong>
        <span style={{ display: "block", marginTop: 6 }}>
          {code ? <><code>{code}</code>: {msg}</> : msg}
        </span>
        {(where || uidUsed || convId) && (
          <span style={{ display: "block", marginTop: 8, opacity: 0.85, fontSize: "0.78rem" }}>
            {where && <>operation: <code>{where}</code></>}
            {uidUsed && <> · uid: <code>{uidUsed.slice(0, 12)}…</code></>}
            {convId && <> · conv: <code>{convId}</code></>}
          </span>
        )}
        {isIndexPending ? (
          <span style={{ display: "block", marginTop: 10 }}>
            Firestore is still building a chat query index — wait ~1–2 minutes and reload.
          </span>
        ) : quotaExceeded ? (
          <span style={{ display: "block", marginTop: 10, opacity: 0.92 }}>
            Firestore quota is exhausted for this project. New chats and messages are blocked until the quota resets or the project is moved to Blaze billing.
          </span>
        ) : (
          <span style={{ display: "block", marginTop: 10, opacity: 0.92 }}>
            If this is <code>permission-denied</code>, redeploy:&nbsp;
            <code style={{ wordBreak: "break-all" }}>firebase deploy --only firestore:rules</code>.
            Old DMs from earlier testing may have wrong member ids — delete the bad&nbsp;
            <code>conversations/dm__…</code>&nbsp;doc in Firebase Console and start fresh.
          </span>
        )}
      </div>
      <button type="button" className="chat-btn-icon" onClick={onDismiss} title="Dismiss">✕</button>
    </div>
  );
}
